import { resolveExceptionContainerLinkId } from 'utils/exceptionContainer'
import { getMeasureUnit } from 'utils/get-measure-unit'

import { WORK_ORDER_CONTAINER_STATUS } from './constants'

import type { ExceptionShape } from '../exceptions/exception-card/types'
import type { MeasureOption, WorkOrderContainer } from './types'

/** True when the container matches the search text by name, barcode, serial, 1-based row number, or measure. */
export const matchesQuery = (container: WorkOrderContainer, index: number, query: string, measureOption?: MeasureOption): boolean => {
  const { volume, unit } = container.measure ?? {}
  const measureLabel = volume != null ? `${volume} ${getMeasureUnit(unit)}` : ''
  return [
    container.name,
    container.barCode,
    container.serialNumber,
    `${index + 1}`,
    measureLabel,
    measureOption?.value,
    measureOption?.unit,
  ].some(field => (field ?? '').toLowerCase().includes(query))
}

/**
 * Rows = containers assigned to this WO (ACTIVE) followed by service containers not assigned to
 * this WO (DISABLED → "Removed from WO"). Disable = unassign from the WO (stays on the service),
 * restore = re-assign.
 */
export const mergeContainers = (
  workOrderContainers: WorkOrderContainer[],
  serviceContainers: WorkOrderContainer[]
): WorkOrderContainer[] => {
  const assignedIds = new Set(workOrderContainers.map(container => container.id))
  const active = workOrderContainers.map(container => ({ ...container, workOrderContainerStatus: WORK_ORDER_CONTAINER_STATUS.ACTIVE }))
  const removed = serviceContainers
    .filter(container => !assignedIds.has(container.id))
    .map(container => ({ ...container, workOrderContainerStatus: WORK_ORDER_CONTAINER_STATUS.DISABLED }))
  return [...active, ...removed]
}

// Embedded container first, then the shared link-id resolver, so a chip and a card never disagree.
export const countExceptionsByContainer = (exceptions: ExceptionShape[]): Record<string, number> => {
  const counts: Record<string, number> = {}
  exceptions.forEach(exception => {
    const containerId = exception.container?.id || resolveExceptionContainerLinkId(exception)
    if (containerId) counts[containerId] = (counts[containerId] ?? 0) + 1
  })
  return counts
}
